import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Users,
  UserCircle,
  Building2,
  Handshake,
  Ticket,
  FileText,
  Receipt,
  Package,
  CreditCard,
  RotateCcw,
  CheckSquare,
  Activity as ActivityIcon,
  Megaphone,
  FileSignature,
  Search,
  Loader2,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';

import { useGlobalSearch } from '@/features/crm/search/hooks/useGlobalSearch';

/**
 * Per-entity rendering + navigation config. `buildHref` lets each entity
 * pick its own target route — Activity has no detail page today (only a
 * list route), so it navigates to the list and the user finds the row
 * there. Everything else goes to `/crm/<entity>/<id>`.
 *
 * Adding an entity here is the only frontend change needed to surface a
 * new backend search-target type — the rest is driven by the response
 * shape. See R30-004 audit gap G.
 */
interface EntityConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  buildHref: (id: string) => string;
}

const ENTITY_CONFIG: Record<string, EntityConfig> = {
  lead: { label: 'Leads', icon: Users, buildHref: (id) => `/crm/leads/${id}` },
  contact: { label: 'Contacts', icon: UserCircle, buildHref: (id) => `/crm/contacts/${id}` },
  account: { label: 'Accounts', icon: Building2, buildHref: (id) => `/crm/accounts/${id}` },
  deal: { label: 'Deals', icon: Handshake, buildHref: (id) => `/crm/deals/${id}` },
  case: { label: 'Cases', icon: Ticket, buildHref: (id) => `/crm/cases/${id}` },
  task: { label: 'Tasks', icon: CheckSquare, buildHref: (id) => `/crm/tasks/${id}` },
  activity: {
    label: 'Activities',
    icon: ActivityIcon,
    // No detail route exists (only /crm/activities list). Navigate to the
    // list page so the user can find the activity row. R30-004 audit gap G.
    buildHref: () => '/crm/activities',
  },
  campaign: { label: 'Campaigns', icon: Megaphone, buildHref: (id) => `/crm/campaigns/${id}` },
  contract: { label: 'Contracts', icon: FileSignature, buildHref: (id) => `/crm/contracts/${id}` },
  quote: { label: 'Quotes', icon: FileText, buildHref: (id) => `/crm/quotes/${id}` },
  sales_order: {
    label: 'Sales Orders',
    icon: Package,
    buildHref: (id) => `/crm/sales-orders/${id}`,
  },
  invoice: { label: 'Invoices', icon: Receipt, buildHref: (id) => `/crm/invoices/${id}` },
  payment: { label: 'Payments', icon: CreditCard, buildHref: (id) => `/crm/payments/${id}` },
  credit_note: {
    label: 'Credit Notes',
    icon: RotateCcw,
    buildHref: (id) => `/crm/credit-notes/${id}`,
  },
};

export function GlobalSearchBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const { data, isLoading } = useGlobalSearch(query);

  // Detect platform for shortcut display
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const modifierKey = isMac ? '⌘' : 'Ctrl';

  // Cmd+K (Mac) / Ctrl+K (Windows/Linux) shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = useCallback(
    (entityType: string, id: string) => {
      setOpen(false);
      setQuery('');
      const config = ENTITY_CONFIG[entityType];
      if (config) {
        navigate({ to: config.buildHref(id) });
      }
    },
    [navigate],
  );

  // Group results by entity type
  const groupedResults: Record<string, NonNullable<typeof data>['results']> = {};
  if (data?.results) {
    for (const result of data.results) {
      const type = result.entityType;
      if (!groupedResults[type]) {
        groupedResults[type] = [];
      }
      groupedResults[type].push(result);
    }
  }

  return (
    <>
      {/* Trigger button in header */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search CRM...</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">{modifierKey}</span>K
        </kbd>
      </button>

      {/* Command palette dialog. shouldFilter is disabled because results
          come from the backend already filtered — cmdk's fuzzy filter would
          otherwise hide rows whose visible text doesn't contain the input
          (e.g. lead matched by phone but rendered with name only).
          See R30-004 audit gap F. */}
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput placeholder="Search CRM records..." value={query} onValueChange={setQuery} />
        <CommandList>
          {isLoading && query.length >= 2 && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          )}

          {!isLoading && query.length >= 2 && data?.total === 0 && (
            <CommandEmpty>No results found for &ldquo;{query}&rdquo;</CommandEmpty>
          )}

          {query.length < 2 && <CommandEmpty>Type at least 2 characters to search...</CommandEmpty>}

          {Object.entries(groupedResults).map(([entityType, results]) => {
            const config = ENTITY_CONFIG[entityType];
            if (!config || !results.length) return null;

            return (
              <CommandGroup key={entityType} heading={config.label}>
                {results.map((result) => {
                  const Icon = config.icon;
                  return (
                    <CommandItem
                      key={`${entityType}-${result.id}`}
                      value={`${entityType}-${result.id}`}
                      onSelect={() => handleSelect(entityType, result.id)}
                    >
                      <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate font-medium">{result.title}</span>
                        {(result.subtitle || result.email) && (
                          <span className="truncate text-xs text-muted-foreground">
                            {[result.subtitle, result.email].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                      {result.status && (
                        <span className="ml-2 text-xs text-muted-foreground shrink-0">
                          {result.status}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
}
